import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import type { ElementType, ReactNode } from 'react';
import styles from './ActionSection.module.scss';
export type ActionSectionAlign = 'start' | 'center';
export type ActionSectionAs = 'section' | 'div';
export type ActionSectionContentWidth = 'default' | 'md';
export type ActionSectionLayout = 'stack' | 'split';
// `page` (the default) is the page colour, the redesign's contact band (Phase
// 4 Q8 A), which reads as the page and so pads nothing of its own; `subtle`
// the grey band (Q23: it was named `brand`, which it never painted); `brand`
// the brand band (Q23, Q15: its solid brand button is the inverse pill);
// `panel` a grey panel inside the container, which keeps its card padding;
// `transparent` no fill. The space around the band is the section rhythm's
// (P above, nothing below); the subtle and brand bands, the inverse tone and
// the conversion variant also pad P inside.
export type ActionSectionSurface = 'page' | 'subtle' | 'brand' | 'panel' | 'transparent';
export type ActionSectionTitleAs = 'h2' | 'h3';
export type ActionSectionTitleScale = 'default' | 'compact' | 'display';
export type ActionSectionTone = 'default' | 'inverse';
// `default` is the contact band; `conversion` the redesign's apply band
// (Phase 4 Q23, incubating: its Phase 5 adopter is ApplyNow).
export type ActionSectionVariant = 'default' | 'conversion';

// One row of the conversion band's term facts (a `<dl>`): the label and its
// value, both plain text (the app resolves dates and terms).
export interface ActionSectionFact {
  label: string;
  value: string;
}
interface ActionSectionBaseProps {
  actions?: ReactNode;
  align?: ActionSectionAlign;
  as?: ActionSectionAs;
  children?: ReactNode;
  contentWidth?: ActionSectionContentWidth;
  // The short Lekton label above the title, in the surface's accent.
  eyebrow?: ReactNode;
  id?: string;
  title?: ReactNode;
  titleAs?: ActionSectionTitleAs;
  titleScale?: ActionSectionTitleScale;
}
export interface ActionSectionDefaultProps extends ActionSectionBaseProps {
  facts?: never;
  layout?: ActionSectionLayout;
  surface?: ActionSectionSurface;
  tone?: ActionSectionTone;
  variant?: 'default';
}

// The conversion band owns its surface (brand), tone and layout (stacked).
export interface ActionSectionConversionProps extends ActionSectionBaseProps {
  facts?: readonly ActionSectionFact[];
  layout?: never;
  surface?: never;
  tone?: never;
  variant: 'conversion';
}
export type ActionSectionProps = ActionSectionDefaultProps | ActionSectionConversionProps;
const ALIGN_CLASS_NAMES: Record<ActionSectionAlign, string | undefined> = {
  start: undefined,
  center: styles['helios-action-section--align-center']
};
const CONTENT_WIDTH_CLASS_NAMES: Record<ActionSectionContentWidth, string> = {
  default: styles['helios-action-section__container--default'],
  md: styles['helios-action-section__container--md']
};
const LAYOUT_CLASS_NAMES: Record<ActionSectionLayout, string | undefined> = {
  stack: undefined,
  split: styles['helios-action-section--layout-split']
};
const SURFACE_CLASS_NAMES: Record<ActionSectionSurface, string | undefined> = {
  page: styles['helios-action-section--surface-page'],
  subtle: styles['helios-action-section--surface-subtle'],
  brand: styles['helios-action-section--surface-brand'],
  panel: styles['helios-action-section--surface-panel'],
  transparent: styles['helios-action-section--surface-transparent']
};
const TITLE_SCALE_CLASS_NAMES: Record<ActionSectionTitleScale, string> = {
  default: styles['helios-action-section__title--default'],
  compact: styles['helios-action-section__title--compact'],
  display: styles['helios-action-section__title--display']
};

// On the root, so the split layout can tell a display-size heading (the
// contact grid) from a compact one (the strip).
const TITLE_SCALE_ROOT_CLASS_NAMES: Record<ActionSectionTitleScale, string> = {
  default: styles['helios-action-section--title-default'],
  compact: styles['helios-action-section--title-compact'],
  display: styles['helios-action-section--title-display']
};
const TONE_CLASS_NAMES: Record<ActionSectionTone, string | undefined> = {
  default: undefined,
  inverse: styles['helios-action-section--tone-inverse']
};
function ActionSection(t0) {
  const $ = _c(48);
  const {
    actions,
    align: t1,
    as: t2,
    children,
    contentWidth: t3,
    eyebrow,
    facts,
    id,
    layout: t4,
    surface: t5,
    title,
    titleAs,
    titleScale: t6,
    tone: t7,
    variant: t8
  } = t0;
  const align = t1 === undefined ? "start" : t1;
  const as = t2 === undefined ? "section" : t2;
  const contentWidth = t3 === undefined ? "default" : t3;
  const layoutProp = t4 === undefined ? "stack" : t4;
  const surfaceProp = t5 === undefined ? "page" : t5;
  const titleScale = t6 === undefined ? "default" : t6;
  const toneProp = t7 === undefined ? "default" : t7;
  const variant = t8 === undefined ? "default" : t8;
  const isConversion = variant === "conversion";
  const layout = isConversion ? "stack" : layoutProp;
  const surface = isConversion ? "brand" : surfaceProp;
  const tone = isConversion ? "default" : toneProp;
  const Component = as as ElementType;
  const Title = (titleAs ?? (titleScale === "display" ? "h2" : "h3")) as 'h2' | 'h3';
  const hasActions = Boolean(actions);
  const hasBody = Boolean(children);
  const surfaceTone = tone === "inverse" ? "dark" : surface === "page" || surface === "subtle" || surface === "brand" ? surface : undefined;
  const band = surfaceTone === "dark" || surfaceTone === "subtle" || surfaceTone === "brand" ? surfaceTone : undefined;
  let t9;
  if ($[0] !== Title || $[1] !== title || $[2] !== titleScale) {
    t9 = title ? <Title className={classNames(styles["helios-action-section__title"], TITLE_SCALE_CLASS_NAMES[titleScale])} data-helios-action-section-title="">{title}</Title> : null;
    $[0] = Title;
    $[1] = title;
    $[2] = titleScale;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const titleElement = t9;
  let t10;
  if ($[4] !== eyebrow) {
    t10 = eyebrow ? <p className={styles["helios-action-section__eyebrow"]}>{eyebrow}</p> : null;
    $[4] = eyebrow;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const eyebrowElement = t10;
  let t11;
  if ($[6] !== children || $[7] !== hasBody) {
    t11 = hasBody ? <div className={styles["helios-action-section__body"]}>{children}</div> : null;
    $[6] = children;
    $[7] = hasBody;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const bodyElement = t11;
  let t12;
  if ($[9] !== facts || $[10] !== isConversion) {
    t12 = isConversion && facts?.length ? <dl className={styles["helios-action-section__facts"]}>{facts.map(_temp)}</dl> : null;
    $[9] = facts;
    $[10] = isConversion;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const factsElement = t12;
  const hasHeading = Boolean(titleElement || eyebrowElement);
  const t13 = band ? "padded" : undefined;
  const t14 = band && styles[`helios-action-section--band-${band}`];
  const t15 = isConversion && styles["helios-action-section--variant-conversion"];
  const t16 = ALIGN_CLASS_NAMES[align];
  const t17 = hasBody && styles["helios-action-section--has-body"];
  const t18 = LAYOUT_CLASS_NAMES[layout];
  const t19 = SURFACE_CLASS_NAMES[surface];
  const t20 = titleElement ? TITLE_SCALE_ROOT_CLASS_NAMES[titleScale] : undefined;
  const t21 = TONE_CLASS_NAMES[tone];
  let t22;
  if ($[12] !== t14 || $[13] !== t15 || $[14] !== t16 || $[15] !== t17 || $[16] !== t18 || $[17] !== t19 || $[18] !== t20 || $[19] !== t21) {
    t22 = classNames(styles["helios-action-section"], t14, t15, t16, t17, t18, t19, t20, t21);
    $[12] = t14;
    $[13] = t15;
    $[14] = t16;
    $[15] = t17;
    $[16] = t18;
    $[17] = t19;
    $[18] = t20;
    $[19] = t21;
    $[20] = t22;
  } else {
    t22 = $[20];
  }
  const t23 = CONTENT_WIDTH_CLASS_NAMES[contentWidth];
  let t24;
  if ($[21] !== t23) {
    t24 = classNames(styles["helios-action-section__container"], t23);
    $[21] = t23;
    $[22] = t24;
  } else {
    t24 = $[22];
  }
  let t25;
  if ($[23] !== bodyElement || $[24] !== eyebrowElement || $[25] !== hasHeading || $[26] !== titleElement) {
    t25 = hasHeading ? <>{eyebrowElement}{titleElement}</> : bodyElement;
    $[23] = bodyElement;
    $[24] = eyebrowElement;
    $[25] = hasHeading;
    $[26] = titleElement;
    $[27] = t25;
  } else {
    t25 = $[27];
  }
  let t26;
  if ($[28] !== t25) {
    t26 = <div className={styles["helios-action-section__content"]}>{t25}</div>;
    $[28] = t25;
    $[29] = t26;
  } else {
    t26 = $[29];
  }
  const t27 = hasHeading ? bodyElement : null;
  let t28;
  if ($[30] !== actions || $[31] !== hasActions) {
    t28 = hasActions ? <div className={styles["helios-action-section__actions"]}>{actions}</div> : null;
    $[30] = actions;
    $[31] = hasActions;
    $[32] = t28;
  } else {
    t28 = $[32];
  }
  let t29;
  if ($[33] !== factsElement || $[34] !== t26 || $[35] !== t27 || $[36] !== t28) {
    t29 = <div className={styles["helios-action-section__wrap"]}>{t26}{t27}{factsElement}{t28}</div>;
    $[33] = factsElement;
    $[34] = t26;
    $[35] = t27;
    $[36] = t28;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  let t30;
  if ($[38] !== t24 || $[39] !== t29) {
    t30 = <div className={t24}>{t29}</div>;
    $[38] = t24;
    $[39] = t29;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  let t31;
  if ($[41] !== Component || $[42] !== id || $[43] !== surfaceTone || $[44] !== t13 || $[45] !== t22 || $[46] !== t30) {
    t31 = <Component id={id} data-helios-band={t13} data-helios-section="" data-helios-surface={surfaceTone} className={t22}>{t30}</Component>;
    $[41] = Component;
    $[42] = id;
    $[43] = surfaceTone;
    $[44] = t13;
    $[45] = t22;
    $[46] = t30;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  return t31;
}
function _temp(fact, index) {
  return <div key={`${fact.label}-${index}`}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>;
}
export default ActionSection;